// CR-005 item 4 (J-191): the landing page, read from the server's metadata at navigation time (the one
// image serves QA and production, so the environment is the server's `APP_ENV`, not a build variable).
// J-209 (inbox 06 PR D item 9): production is read from `/me/access`, which every signed-in user may
// read, so an account metadata refuses is never taken for "not production".
import { fetchClient } from "@/api/client";
import { ApiReadError } from "@/api/resilient-fetch";
import { groupLandingPath, isGroupScoped, withGroupScope } from "@/lib/group-access";
import { landingPath } from "./default-aco";

/**
 * Metadata, or undefined when the server refuses it (403: no ACO to open). J-215 (inbox 22 item 1): any
 * other failure throws, so "/" shows its error page with Try again instead of "no ACO access".
 */
async function readMetadata() {
  const { data, error, response } = await fetchClient.GET("/api/v1/metadata");
  if (response.status === 403) return undefined;
  if (error || data === undefined) throw new ApiReadError(response.status, error?.error);
  return data;
}

/**
 * J-238: the Quality report on the first granted group for a user who sees only its groups, else
 * undefined. A failed `/me/access` read is not a group scope: the server still refuses another group.
 */
async function groupLanding(): Promise<string | undefined> {
  const { data } = await fetchClient.GET("/api/v1/me/access");
  if (data === undefined) return undefined;
  const access = withGroupScope(data);
  return isGroupScoped(access) ? groupLandingPath(access.groups) : undefined;
}

/** Where "/" and the end of two-factor sign-in go; `canLoadData` is the role's `acoData.load`. */
export async function landingRoute(canLoadData: boolean): Promise<string> {
  return (await groupLanding()) ?? landingPath(await readMetadata(), canLoadData);
}

/**
 * True unless the server said it is not production: a failed read counts as production, so the
 * Playground is never shown on a guess.
 */
export async function serverIsProduction(): Promise<boolean> {
  const { data } = await fetchClient.GET("/api/v1/me/access");
  return data?.appEnv !== "development" && data?.appEnv !== "test" && data?.appEnv !== "qa";
}
